import type { ReactNode } from 'react';

import { PresentationProvider, useInheritedPresentation } from './usePresentation';

/**
 * Cascades a selected state to all descendent components.
 *
 * @example
 *   <SelectedProvider value={isSelected}>
 *     <Badge>Tinted brand while the ancestor is selected</Badge>
 *   </SelectedProvider>
 */
export function SelectedProvider({ value, children }: { value?: boolean; children: ReactNode }) {
  return <PresentationProvider selected={value}>{children}</PresentationProvider>;
}

/**
 * Returns the current selected state from context if defined, otherwise the default.
 */
export const useSelected = (controlledValue?: boolean, defaultValue: boolean = false) => {
  const { selected } = useInheritedPresentation();
  return controlledValue ?? selected ?? defaultValue;
};

/**
 * Stops a cascading selected state, for children that own their own selection. Leaves any
 * inherited variant alone.
 */
export function ClearSelectedContext({ children }: { children: ReactNode }) {
  return <PresentationProvider selected={false}>{children}</PresentationProvider>;
}
